/**
 * Opens a PDF the API serves through a short-lived storage link
 * (e.g. GET .../pod/events/{id}/packet-link -> { url }).
 *
 * Used where the file can be bigger than the API may return directly:
 * Lambda refuses any response over 6 MB, so a proof-of-delivery packet
 * with four or more photos never reached the browser through openPdf().
 *
 * The tab is opened synchronously, inside the click, so the popup blocker
 * allows it; it shows a "building" note until the link comes back, then
 * navigates to the PDF. On failure the tab is closed and the error is
 * re-thrown for the caller to show.
 *
 * Returns { opened: true } or, when the browser refused the tab,
 * { blocked: true, url } so the caller can say so.
 */
export default async function openLinkedPdf(fetchLink, win = window, waitingText = 'Building the PDF…') {
  const tab = win.open('', '_blank');
  if (tab) {
    try { tab.opener = null; } catch { /* cross-origin: nothing to clear */ }
    try {
      tab.document.title = waitingText;
      tab.document.body.style.font = '15px system-ui, sans-serif';
      tab.document.body.style.padding = '24px';
      tab.document.body.textContent = waitingText;
    } catch { /* the tab still works without the note */ }
  }
  let link;
  try {
    link = await fetchLink();
    if (!link || !link.url) throw new Error('The server did not return a link.');
  } catch (err) {
    if (tab) { try { tab.close(); } catch { /* already gone */ } }
    throw err;
  }
  if (!tab) return { blocked: true, url: link.url };
  tab.location.replace(link.url);
  return { opened: true };
}
